import type { ComponentType } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ChevronRight, Gauge, type LucideIcon } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { useOosEmailTemplate } from '@/hooks/useOosEmailTemplate'
import { OosEmailTemplateCard } from './OosEmailTemplateCard'

/**
 * One email the app sends that a site can word for itself. A new email is one
 * more entry here: its list row reads `useIsDefault`, and `Editor` edits it.
 */
interface EmailTemplateType {
  /** URL value (?email=…). */
  key: string
  name: string
  description: string
  icon: LucideIcon
  useIsDefault: () => boolean | undefined
  Editor: ComponentType
}

const EMAIL_TEMPLATE_TYPES: readonly EmailTemplateType[] = [
  {
    key: 'oos-alert',
    name: 'Out-of-specification alert',
    description: "Sent to a field's groups when a value is out of specification.",
    icon: Gauge,
    useIsDefault: () => useOosEmailTemplate().data?.isDefault,
    Editor: OosEmailTemplateCard,
  },
]

/**
 * Configure → Email Groups → Email templates: the emails a site can word for
 * itself. The list on the left names each one; the selected one is edited on
 * the right. The selection lives in the URL so a link can open one directly.
 */
export function EmailTemplatesSettings() {
  const [params, setParams] = useSearchParams()
  const selected =
    EMAIL_TEMPLATE_TYPES.find(t => t.key === params.get('email')) ?? EMAIL_TEMPLATE_TYPES[0]

  const select = (key: string) =>
    setParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.set('email', key)
        return next
      },
      { replace: true },
    )

  return (
    <div className='grid gap-6 lg:grid-cols-[18rem_1fr]'>
      <Card className='h-fit p-2'>
        <p className='px-2 pb-2 pt-1 text-xs font-medium uppercase tracking-wide text-muted-foreground'>Emails</p>
        <nav aria-label='Email templates' className='space-y-1'>
          {EMAIL_TEMPLATE_TYPES.map(t => (
            <TemplateRow key={t.key} type={t} active={t.key === selected.key} onSelect={() => select(t.key)} />
          ))}
        </nav>
      </Card>
      <selected.Editor />
    </div>
  )
}

function TemplateRow({
  type,
  active,
  onSelect,
}: {
  type: EmailTemplateType
  active: boolean
  onSelect: () => void
}) {
  const isDefault = type.useIsDefault()
  const Icon = type.icon
  return (
    <button
      type='button'
      onClick={onSelect}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex w-full items-start gap-3 rounded-md px-2.5 py-2 text-left transition-colors',
        active ? 'bg-primary/10' : 'hover:bg-accent',
      )}
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', active ? 'text-primary' : 'text-muted-foreground')} />
      <span className='min-w-0 flex-1'>
        <span className='flex items-start justify-between gap-2'>
          <span className={cn('text-sm leading-snug', active && 'font-medium')}>{type.name}</span>
          {isDefault !== undefined && (
            <span
              className={cn(
                'mt-0.5 shrink-0 rounded-full px-1.5 py-px text-[10px] font-medium',
                isDefault ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary',
              )}
            >
              {isDefault ? 'Default' : 'Custom'}
            </span>
          )}
        </span>
        <span className='mt-0.5 block text-xs text-muted-foreground'>{type.description}</span>
      </span>
      <ChevronRight className={cn('mt-0.5 h-4 w-4 shrink-0', active ? 'text-primary' : 'text-transparent')} />
    </button>
  )
}
